import type { ScreenType } from '@frontend/wizard/types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LuChevronLeft, LuChevronRight } from 'react-icons/lu';

import { wizardModalClassNames } from './wizard-modal.classes';

export interface WizardModalHeaderStep {
    screen: ScreenType;
    label: string;
    status: 'completed' | 'current' | 'upcoming';
}

interface WizardModalHeaderProps {
    title: string;
    steps: WizardModalHeaderStep[];
}

const statusClassName: Record<WizardModalHeaderStep['status'], string> = {
    completed: 'border-brand-primary bg-brand-primary text-background-surface',
    current: 'border-brand-primary bg-background-surface text-brand-primary',
    upcoming: 'border-border-default bg-background-default text-foreground-muted'
};

export const WizardModalHeader = (props: WizardModalHeaderProps) => {
    const currentStepIndex = useMemo(() => props.steps.findIndex((step) => step.status === 'current'), [props.steps]);
    const stepsRailRef = useRef<HTMLOListElement | null>(null);
    const currentStepRef = useRef<HTMLLIElement | null>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const updateScrollState = useCallback(() => {
        const rail = stepsRailRef.current;
        if (!rail) {
            setCanScrollLeft(false);
            setCanScrollRight(false);
            return;
        }

        const maxScrollLeft = rail.scrollWidth - rail.clientWidth;
        setCanScrollLeft(rail.scrollLeft > 1);
        setCanScrollRight(rail.scrollLeft < maxScrollLeft - 1);
    }, []);

    useEffect(() => {
        currentStepRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center'
        });

        window.requestAnimationFrame(updateScrollState);
    }, [currentStepIndex]);

    useEffect(() => {
        updateScrollState();

        const rail = stepsRailRef.current;
        if (!rail) return;

        const resizeObserver = new ResizeObserver(updateScrollState);
        resizeObserver.observe(rail);

        return () => {
            resizeObserver.disconnect();
        };
    }, [props.steps.length, updateScrollState]);

    const scrollToStart = () => {
        stepsRailRef.current?.scrollTo({ left: 0, behavior: 'smooth' });
        window.setTimeout(updateScrollState, 350);
    };

    const scrollToEnd = () => {
        const rail = stepsRailRef.current;
        if (!rail) return;

        rail.scrollTo({ left: rail.scrollWidth - rail.clientWidth, behavior: 'smooth' });
        window.setTimeout(updateScrollState, 350);
    };

    return (
        <div className={wizardModalClassNames.header}>
            <h2 className='text-foreground-default m-0 max-w-full truncate text-xl font-semibold'>{props.title}</h2>

            {props.steps.length > 0 && (
                <div className='relative w-full min-w-0'>
                    {canScrollLeft && (
                        <div className='from-background-surface pointer-events-none absolute top-0 bottom-0 left-0 z-10 w-12 bg-gradient-to-r to-transparent' />
                    )}
                    {canScrollRight && (
                        <div className='from-background-surface pointer-events-none absolute top-0 right-0 bottom-0 z-10 w-12 bg-gradient-to-l to-transparent' />
                    )}

                    {canScrollLeft && (
                        <button
                            type='button'
                            onClick={scrollToStart}
                            className='border-border-default text-foreground-default hover:bg-background-default bg-background-surface absolute top-1/2 left-0 z-20 flex size-7 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border shadow-sm transition-colors'
                            aria-label='Scroll to first wizard step'>
                            <LuChevronLeft className='size-4' />
                        </button>
                    )}

                    <ol
                        ref={stepsRailRef}
                        onScroll={updateScrollState}
                        className='wizard-modal-steps m-0 flex w-full max-w-full min-w-0 list-none items-center gap-2 overflow-x-auto overscroll-x-contain p-0'>
                        {props.steps.map((step, index) => (
                            <li
                                key={`${step.screen}-${index}`}
                                ref={step.status === 'current' ? currentStepRef : undefined}
                                className='flex min-w-0 shrink-0 items-center gap-2'>
                                <div
                                    className={`flex size-7 items-center justify-center rounded-full border text-sm font-semibold ${statusClassName[step.status]}`}>
                                    {index + 1}
                                </div>
                                <span
                                    className={`max-w-44 truncate text-sm font-medium ${
                                        step.status === 'upcoming' ? 'text-foreground-muted' : 'text-foreground-default'
                                    }`}>
                                    {step.label}
                                </span>
                                {index < props.steps.length - 1 && <div className='bg-border-default h-px w-8 shrink-0' />}
                            </li>
                        ))}
                    </ol>

                    {canScrollRight && (
                        <button
                            type='button'
                            onClick={scrollToEnd}
                            className='border-border-default text-foreground-default hover:bg-background-default bg-background-surface absolute top-1/2 right-0 z-20 flex size-7 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border shadow-sm transition-colors'
                            aria-label='Scroll to last wizard step'>
                            <LuChevronRight className='size-4' />
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};
